import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useCall } from './CallProvider';
import JitsiEmbed from './JitsiEmbed';
import useWebRTC from './useWebRTC';
import useCallRecording from '../../hooks/useCallRecording';
import ActiveCallBar from './ActiveCallBar';
import IncomingCallModal from './IncomingCallModal';

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9998,
    background: 'rgba(0,0,0,0.85)', display: 'flex',
    flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  },
  audioOnly: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', color: '#fff', gap: 16,
  },
  avatar: {
    width: 100, height: 100, borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 42, color: '#fff',
  },
  peerName: { fontSize: 22, fontWeight: 600 },
  videoContainer: { position: 'relative', width: '100%', height: '100%' },
  remoteVideo: { width: '100%', height: '100%', objectFit: 'cover' },
  localVideo: {
    position: 'absolute', bottom: 80, right: 16,
    width: 120, height: 90, borderRadius: 8,
    objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)',
  },
  endedMsg: {
    position: 'fixed', inset: 0, zIndex: 9998,
    background: 'rgba(0,0,0,0.7)', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontSize: 18, fontWeight: 500,
  },
};

export default function CallOverlay() {
  const { callState, callInfo, callMethod, localStream, CALL_STATES } = useCall();
  const { isRecording, startRecording, stopRecording } = useCallRecording();
  const [remoteStreamObj, setRemoteStreamObj] = useState(null);
  const remoteAudioRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);

  const handleRemoteStream = useCallback((stream) => {
    setRemoteStreamObj(stream);
  }, []);

  useWebRTC({ onRemoteStream: handleRemoteStream });

  // Attach remote stream to audio/video element
  useEffect(() => {
    if (remoteStreamObj && remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = remoteStreamObj;
    }
    if (remoteStreamObj && remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = remoteStreamObj;
    }
  }, [remoteStreamObj]);

  // Attach local stream to local video preview
  useEffect(() => {
    if (localStream.current && localVideoRef.current) {
      localVideoRef.current.srcObject = localStream.current;
    }
  }, [localStream, callState]);

  const handleToggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording(callInfo?.callId, null, callInfo?.fromName || callInfo?.to);
    } else {
      const local = localStream.current;
      const remote = remoteStreamObj;
      startRecording(local, remote);
    }
  }, [isRecording, startRecording, stopRecording, callInfo, localStream, remoteStreamObj]);

  // Auto-save every WebRTC call (not Jitsi — no stream access)
  useEffect(() => {
    if (callMethod === 'jitsi') return;
    if (callState === CALL_STATES.ACTIVE && localStream.current) {
      startRecording(localStream.current, remoteStreamObj);
    }
    if (callState === CALL_STATES.ENDED && isRecording) {
      stopRecording(callInfo?.callId, null, callInfo?.fromName || callInfo?.to);
    }
  }, [callState, callMethod, remoteStreamObj, isRecording, startRecording, stopRecording, callInfo, localStream, CALL_STATES]);

  // Ringing screen
  if (callState === CALL_STATES.RINGING) {
    return <IncomingCallModal />;
  }

  // Brief "call ended" flash
  if (callState === CALL_STATES.ENDED) {
    const reason = callInfo?.endReason;
    const msg = reason === 'rejected' ? 'Call Declined'
      : reason === 'no-answer' ? 'No Answer'
      : reason === 'offline' ? 'User Offline'
      : reason === 'claimed' ? 'Answered on Another Device'
      : 'Call Ended';
    return <div style={styles.endedMsg}>{msg}</div>;
  }

  // Active call
  if (callState !== CALL_STATES.ACTIVE && callState !== CALL_STATES.CONNECTING) return null;

  const isJitsi = callMethod === 'jitsi';
  const isVideoCall = callInfo?.callType === 'video';
  const peerName = callInfo?.fromName || callInfo?.to || 'Call';
  const initial = peerName[0]?.toUpperCase() || '?';

  return (
    <>
      <div style={styles.overlay}>
        {isJitsi ? (
          <div style={styles.videoContainer}>
            <JitsiEmbed />
          </div>
        ) : isVideoCall ? (
          <div style={styles.videoContainer}>
            <video ref={remoteVideoRef} style={styles.remoteVideo} autoPlay playsInline />
            <video ref={localVideoRef} style={styles.localVideo} autoPlay playsInline muted />
            <audio ref={remoteAudioRef} autoPlay />
          </div>
        ) : (
          <div style={styles.audioOnly}>
            <div style={styles.avatar}>{initial}</div>
            <span style={styles.peerName}>{peerName}</span>
            <audio ref={remoteAudioRef} autoPlay />
          </div>
        )}
      </div>
      <ActiveCallBar isRecording={isRecording} onToggleRecording={handleToggleRecording} />
    </>
  );
}
